// src/context/AuthContext.tsx
import React, { createContext, useState, useEffect, useContext } from 'react';
import { getCurrentUser, login, register, logout, getStoredUser } from '../services/authService';

interface AuthContextType {
  user: any;
  loading: boolean;
  error: string | null;
  isAuthenticated: boolean;
  login: (contact: string, password: string) => Promise<any>;
  register: (userData: any) => Promise<any>;
  logout: () => void;
  clearError: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth doit être utilisé à l\'intérieur d\'un AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<any>(getStoredUser());
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const verifyAuth = async () => {
      try {
        const token = localStorage.getItem('token');
        if (token) {
          const userData = await getCurrentUser();
          if (userData) {
            setUser(userData);
            localStorage.setItem('user', JSON.stringify(userData));
          } else {
            setUser(null);
            localStorage.removeItem('user');
          }
        } else {
          setUser(null);
        }
      } catch (err) {
        console.error("Erreur lors de la vérification de l'authentification:", err);
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    verifyAuth();
  }, []);

  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'token' && !e.newValue) {
        setUser(null);
      } else if (e.key === 'user' && e.newValue) {
        setUser(JSON.parse(e.newValue));
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const handleLogin = async (contact: string, password: string) => {
    setLoading(true);
    setError(null);
    try {
      const data = await login({ contact, password });
      setUser(data.user);
      return data;
    } catch (err: any) {
      console.error("Erreur complète lors de la connexion:", err);
      if (err.response) {
        setError(err.response.data?.detail || 'Erreur de connexion');
      } else if (err.request) {
        setError('Le serveur ne répond pas. Veuillez réessayer plus tard.');
      } else {
        setError('Une erreur s\'est produite. Veuillez réessayer.');
      }
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (userData: any) => {
    setLoading(true);
    setError(null);
    try {
      const data = await register(userData);
      return data;
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Erreur d\'inscription');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    setUser(null);
  };

  const clearError = () => {
    setError(null);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        error,
        isAuthenticated: !!user,
        login: handleLogin,
        register: handleRegister,
        logout: handleLogout,
        clearError
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;